import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Card } from '../../../ui/card';

const formatValue = (value, type) => {
  if (type === 'currency') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }
  if (type === 'number' && value >= 1000) {
    return new Intl.NumberFormat('en-US', { notation: 'compact' }).format(
      value,
    );
  }
  return value?.toLocaleString() || '0';
};

export function KPICard({
  title,
  value,
  growth,
  icon: Icon,
  color = 'blue',
  type = 'number',
  onClick,
  delay = 0,
}) {
  const colorClasses = {
    blue: { bg: 'bg-blue-50', icon: 'text-blue-600', ring: 'ring-blue-100' },
    purple: {
      bg: 'bg-purple-50',
      icon: 'text-purple-600',
      ring: 'ring-purple-100',
    },
    green: {
      bg: 'bg-emerald-50',
      icon: 'text-emerald-600',
      ring: 'ring-emerald-100',
    },
    amber: {
      bg: 'bg-amber-50',
      icon: 'text-amber-600',
      ring: 'ring-amber-100',
    },
    indigo: {
      bg: 'bg-indigo-50',
      icon: 'text-indigo-600',
      ring: 'ring-indigo-100',
    },
    rose: { bg: 'bg-rose-50', icon: 'text-rose-600', ring: 'ring-rose-100' },
  };

  const colors = colorClasses[color] || colorClasses.blue;

  const GrowthIcon =
    growth > 0 ? TrendingUp : growth < 0 ? TrendingDown : Minus;
  const growthColor =
    growth > 0
      ? 'text-emerald-600'
      : growth < 0
        ? 'text-red-500'
        : 'text-gray-400';
  const growthBg =
    growth > 0 ? 'bg-emerald-50' : growth < 0 ? 'bg-red-50' : 'bg-stone-50';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.3 }}
    >
      <Card
        className="p-5 bg-white border-red-200/80 hover:shadow-lg hover:shadow-stone-200/50 transition-all duration-300 cursor-pointer group"
        onClick={onClick}
      >
        <div className="flex items-start justify-between mb-3">
          <div
            className={`w-10 h-10 ${colors.bg} rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform ring-1 ${colors.ring}`}
          >
            <Icon className={`w-5 h-5 ${colors.icon}`} />
          </div>
          {growth !== undefined && growth !== null && (
            <div
              className={`flex items-center gap-1 px-2 py-1 rounded-lg ${growthBg}`}
            >
              <GrowthIcon className={`w-3 h-3 ${growthColor}`} />
              <span className={`text-xs font-semibold ${growthColor}`}>
                {Math.abs(growth)}%
              </span>
            </div>
          )}
        </div>
        <div className="space-y-1">
          <motion.p
            className="text-2xl font-bold text-gray-900 font-outfit"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: delay + 0.1 }}
          >
            {formatValue(value, type)}
          </motion.p>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            {title}
          </p>
        </div>
      </Card>
    </motion.div>
  );
}

export default KPICard;
